// Sub-components for WeeklyReviewModal: InboxZeroStep + ReflectionStep.
// GoalsProjectsStep lives in weekly-review-goals-step.tsx (size split).
// SummaryStep + StatCard also here (small, co-located with other display-only steps).

import { useState } from "react";
import { CheckCircle2, Circle, Trash2, ArrowRight } from "lucide-react";
import { formatMoney, todayIso } from "../lib/date";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { InboxItem } from "../types";
import type { weekStats } from "./review-derive";

// ── Step 1: Inbox zero ────────────────────────────────────────────────────────

interface InboxStepProps {
  api: LifeOsApi;
  onInboxCleared: (cleared: boolean) => void;
}

export function InboxZeroStep({ api, onInboxCleared }: InboxStepProps) {
  const inbox = api.state.inbox;
  const [cleared, setCleared] = useState(false);

  function handleDelete(item: InboxItem) {
    api.actions.remove("inbox", item.id);
  }

  function handleConvertToTask(item: InboxItem) {
    api.actions.convertInboxItem(item.id, {
      type: "task",
      item: {
        title: item.text,
        dueDate: todayIso(),
        status: "todo",
        priority: "medium",
        estimateMinutes: 30,
        subtasks: [],
      },
    });
  }

  function toggleCleared() {
    const next = !cleared;
    setCleared(next);
    onInboxCleared(next);
  }

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-ink-500">Xử lý từng mục: convert sang việc hoặc xoá.</p>
      {inbox.length === 0 ? (
        <div className="rounded-2xl bg-brand-50 px-4 py-6 text-center">
          <p className="text-[14px] font-bold text-brand-700">Inbox trống rỗng!</p>
          <p className="text-[12px] text-ink-500 mt-1">Tuyệt vời — không còn gì cần xử lý.</p>
        </div>
      ) : (
        <div className="max-h-52 overflow-y-auto space-y-2 pr-1">
          {inbox.map((item) => (
            <div key={item.id} className="flex items-start gap-2 rounded-2xl bg-ink-50 px-3 py-2.5">
              <p className="flex-1 text-[13px] text-ink-800 line-clamp-2">{item.text}</p>
              <div className="flex shrink-0 gap-1">
                <button
                  onClick={() => handleConvertToTask(item)}
                  title="Chuyển thành việc"
                  className="flex items-center gap-1 rounded-xl bg-brand-100 px-2 py-1 text-[11px] font-bold text-brand-700 hover:bg-brand-200"
                >
                  <ArrowRight className="h-3 w-3" /> Việc
                </button>
                <button
                  onClick={() => handleDelete(item)}
                  title="Xoá"
                  className="grid h-7 w-7 place-items-center rounded-xl text-ink-400 hover:bg-rose-50 hover:text-rose-500"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={toggleCleared}
        className={`flex w-full items-center gap-2 rounded-2xl border px-3 py-2.5 text-[13px] font-semibold transition ${
          cleared
            ? "border-brand-400 bg-brand-50 text-brand-700"
            : "border-ink-200 bg-white text-ink-600 hover:bg-ink-50"
        }`}
      >
        {cleared ? <CheckCircle2 className="h-4 w-4 text-brand-500" /> : <Circle className="h-4 w-4 text-ink-400" />}
        Đã xử lý xong inbox
      </button>
    </div>
  );
}

// ── Step 3: Reflection ────────────────────────────────────────────────────────

interface ReflectionStepProps {
  reflection: string;
  wins: string;
  blockers: string;
  onReflection: (v: string) => void;
  onWins: (v: string) => void;
  onBlockers: (v: string) => void;
  /** Scope drives copy ("Tuần này" vs "Tháng này"). */
  scope?: "week" | "month";
}

export function ReflectionStep({ reflection, wins, blockers, onReflection, onWins, onBlockers, scope = "week" }: ReflectionStepProps) {
  const period = scope === "month" ? "Tháng này" : "Tuần này";
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1">
        <label className="text-[12px] font-semibold text-ink-600">Cảm nhận / chiêm nghiệm *</label>
        <textarea
          value={reflection}
          onChange={(e) => onReflection(e.target.value)}
          placeholder={`${period} diễn ra như thế nào? Bạn học được gì?`}
          className="w-full resize-y rounded-2xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-[13.5px] leading-relaxed text-ink-800 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-200"
          rows={6}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[12px] font-semibold text-ink-600">Điểm sáng (tuỳ chọn)</label>
        <textarea
          value={wins}
          onChange={(e) => onWins(e.target.value)}
          placeholder="Thành tích, niềm vui, điều tốt đẹp…"
          className="w-full resize-y rounded-2xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-[13.5px] leading-relaxed text-ink-800 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-200"
          rows={3}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[12px] font-semibold text-ink-600">Rào cản (tuỳ chọn)</label>
        <textarea
          value={blockers}
          onChange={(e) => onBlockers(e.target.value)}
          placeholder="Điều gì đang cản trở bạn?"
          className="w-full resize-y rounded-2xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-[13.5px] leading-relaxed text-ink-800 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-200"
          rows={3}
        />
      </div>
    </div>
  );
}

// ── Step 4: Summary (display-only) ────────────────────────────────────────────

type StatsShape = ReturnType<typeof weekStats>;

interface SummaryStepProps {
  scope: "week" | "month";
  stats: StatsShape;
  reflection: string;
}

export function SummaryStep({ scope, stats, reflection }: SummaryStepProps) {
  return (
    <div className="space-y-3">
      <p className="text-[12px] text-ink-500">
        {scope === "week" ? "Tóm tắt tuần của bạn:" : "Tóm tắt tháng của bạn:"}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <StatCard label="Việc hoàn thành" value={String(stats.doneCount)} />
        <StatCard label="Việc dời lại" value={String(stats.deferredCount)} />
        <StatCard label="Thói quen" value={String(stats.habitCompletions)} />
        <StatCard label="Dòng tiền" value={formatMoney(stats.netCashflow)} positive={stats.netCashflow >= 0} />
        <StatCard label="Mục tiêu đang theo" value={String(stats.activeGoals)} />
        <StatCard label="Dự án đang chạy" value={String(stats.activeProjects)} />
      </div>
      {reflection.trim() && (
        <div className="rounded-2xl bg-brand-50 px-3 py-2.5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-brand-700 mb-1">Chiêm nghiệm</p>
          <p className="whitespace-pre-line text-[13px] text-ink-700 line-clamp-4">{reflection.trim()}</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  const valueColor = positive === undefined ? "text-ink-800" : positive ? "text-emerald-600" : "text-rose-600";
  return (
    <div className="rounded-2xl bg-ink-50 px-3 py-2.5">
      <p className="text-[11px] text-ink-500">{label}</p>
      <p className={`text-[15px] font-bold ${valueColor}`}>{value}</p>
    </div>
  );
}
